// Special component that handles the authentication callback from route switching
// Cleaned on 9/6

import { jwtDecode } from 'jwt-decode';
import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApplicationTrackerContext } from '../contexts/ApplicationTrackerContext';
import { useAuth } from '../contexts/AuthContext';
import { GlobalContext } from '../contexts/GlobalContext';
import axios from '../utils/axiosConfig';

function AuthCallback() {
  const navigate = useNavigate();
  const globalContext = useContext(GlobalContext);
  const { setEmailScrapingAllowed } = useContext(ApplicationTrackerContext);
  const { checkAuth, setTokens, accessToken } = useAuth();
  const processedRef = useRef(false);

  /**
   * Creates a new user in the database if the user does not exist already.
   * Returns the user ID if successful, otherwise throws an error.
   */
  async function createUser(userEmail, userFirstName, userMiddleName, userLastName, accessToken) {
    const response = await axios.post('/api/v1/user/create', {
      user: {
        email: userEmail,
        first_name: userFirstName,
        middle_name: userMiddleName,
        last_name: userLastName,
      },
      access_token: accessToken,
    });

    const data = response.data;

    if (data.message === 'User created successfully') {
      return data.userID;
    } else {
      throw new Error('User creation failed');
    }
  }

  /**
   * Updates the user preferences in the database.
   * Returns nothing if successful, otherwise throws an error.
   */
  function updateUserPreferences(userId, userPreferences, accessToken) {
    console.log('updating user preferences');
    console.log(globalContext.onboardingInfo);
    console.log(userPreferences);
    return axios
      .put(`/api/v1/user/preferences/${userId}`, {
        userPreferences: {
          industries: userPreferences.industries,
          role_type: userPreferences.roleType,
        },
        access_token: accessToken,
      })
      .then((response) => response.data)
      .catch((error) => console.error('Error:', error));
  }

  useEffect(() => {
    const handleCallback = async () => {
      if (processedRef.current) {
        console.log('Callback already processed, skipping...');
        return;
      }
      processedRef.current = true;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const is_scraping = localStorage.getItem('emailScrapingAllowed');
        const returnPath = localStorage.getItem('returnToPath');
        const rememberMe = localStorage.getItem('googleOAuthRememberMe') === 'true';

        if (!code) {
          console.error('No code found in URL');
          navigate('/login');
          return;
        }

        // Define scopes based on whether email scraping is requested
        const scopes =
          is_scraping === 'true'
            ? [
                'openid',
                'https://www.googleapis.com/auth/userinfo.profile',
                'https://www.googleapis.com/auth/userinfo.email',
                'https://www.googleapis.com/auth/gmail.readonly',
              ]
            : [
                'openid',
                'https://www.googleapis.com/auth/userinfo.profile',
                'https://www.googleapis.com/auth/userinfo.email',
              ];

        // Exchange code for tokens
        const response = await axios.post('/api/v1/auth/google/callback', {
          code: code,
          scopes: scopes,
          remember_me: rememberMe,
        });

        const data = response.data;

        // Handle email credentials response (no access token, just email access granted)
        if (data.email_access_granted) {
          console.log('Email access granted successfully');

          // Set email scraping as allowed
          setEmailScrapingAllowed(true);

          // Clean up localStorage
          localStorage.removeItem('emailScrapingAllowed');
          localStorage.removeItem('googleOAuthRememberMe');

          // Navigate back to where they came from
          navigate(returnPath || '/application-tracking');
          return;
        }

        // Handle regular authentication response
        if (data.access_token) {
          console.log('Received access token from Google OAuth callback');

          // Store access token in memory via auth context
          setTokens(data.access_token);
          console.log('Access token set in context');

          // Safely get the user ID
          const userId = data.user?.id || data.user?.userID;
          if (userId) {
            localStorage.setItem('userID', userId);
          } else {
            const decodedToken = jwtDecode(data.access_token);
            if (decodedToken.sub) {
              localStorage.setItem('userID', decodedToken.sub);
            }
          }

          console.log('AuthCallback: Authentication successful, proceeding with navigation');

          // Clean up remember me preference from localStorage
          localStorage.removeItem('googleOAuthRememberMe');

          if (is_scraping === 'true') {
            try {
              const decodedToken = jwtDecode(data.access_token);
              const userId = decodedToken.sub;
              const scrapingResponse = await axios.put(
                `/api/v1/user/allow-email-scraping/${userId}`,
                {
                  allow_email_scraping: true,
                }
              );

              const scrapingData = scrapingResponse.data;
              if (scrapingData && scrapingData.allow_email_scraping) {
                setEmailScrapingAllowed(true);
                localStorage.removeItem('emailScrapingAllowed');
                navigate('/application-tracking');
                return;
              } else {
                console.error('Email scraping not enabled in response');
                navigate('/application-tracking');
                return;
              }
            } catch (error) {
              console.error('Error enabling email scraping:', error);
              navigate('/application-tracking');
              return;
            }
          } else {
            navigate(returnPath || '/');
            return;
          }
        } else {
          console.error('Authentication failed');
          navigate('/login');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        let errorMessage = 'Authentication failed. Please try again.';

        // Try to extract error message from the error
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }

        navigate('/login', {
          state: {
            error: errorMessage,
            showAuthForm: true,
          },
        });
      }
    };

    handleCallback();

    return () => {
      processedRef.current = true;
    };
  }, [navigate, globalContext, setEmailScrapingAllowed, checkAuth, setTokens]);

  return null;
}

export default AuthCallback;
